import {
  convertSearchSpringCarouselProductPricingToMarketPrice,
  getCarouselItems,
  isSearchSpringCarouselProductResponse
} from '../helpers/searchSpringHelpers'
import { generateBeacon, generateBeaconProducts, postBeaconTracking } from '../helpers/searchspring-tracking'
import { getCookie } from '../helpers/cookies'
import { SSCarouselResult, Payload } from '../search-spring/types/types'

declare var Shopify: { currency: { active: string } }

type CarouselRequest = {
  profile: string
  productId?: string
  limit?: string
}

/*
  A page can hold more than one carousel on the same profile - the compact PDP
  block is output in both the desktop and the mobile column. Share the request
  and the beacons between them so Searchspring only ever sees one of each.
*/
const inFlight = new Map<string, Promise<SSCarouselResult[]>>()
const beaconsSent = new Set<string>()

const fetchProducts = async ({ profile, productId, limit }: CarouselRequest): Promise<SSCarouselResult[]> => {
  const payload = { limits: limit || '8', tags: profile } as Payload
  const ssViewedProducts = getCookie('ssViewedProducts')
  const ssShopperId = getCookie('ssShopperId')
  const ssCartProducts = getCookie('ssCartProducts')

  if (productId) payload.productId = productId
  if (ssViewedProducts) payload.lastViewed = ssViewedProducts
  if (ssShopperId) payload.shopper = ssShopperId
  if (ssCartProducts) payload.cart = ssCartProducts

  const response = await getCarouselItems(payload)

  if (!isSearchSpringCarouselProductResponse(response)) throw new Error('Unexpected Searchspring carousel response')

  if (Shopify.currency.active !== 'AUD') {
    const pricedResponse = await convertSearchSpringCarouselProductPricingToMarketPrice(response[0])
    return pricedResponse.results || response[0].results
  }

  return response[0].results
}

export const getCarouselProducts = (request: CarouselRequest): Promise<SSCarouselResult[]> => {
  const key = `${request.profile}|${request.productId || ''}|${request.limit || ''}`
  const cached = inFlight.get(key)

  if (cached) return cached

  const promise = fetchProducts(request).finally(() => inFlight.delete(key))
  inFlight.set(key, promise)

  return promise
}

/* `type` is the profile-level beacon, e.g. profile.render or profile.impression. */
export const trackProfileOnce = (type: string, sku: string, profile: string, products: SSCarouselResult[]) => {
  const key = `${type}|${profile}|${sku}`

  if (beaconsSent.has(key) || products.length === 0) return

  beaconsSent.add(key)
  postBeaconTracking([
    generateBeacon(sku, profile, type),
    ...generateBeaconProducts(products, profile, type.replace('profile.', 'profile.product.'), null)
  ])
}
